import React from 'react'
import { getImgProps } from '@superhi/design'

import { API } from '../../../../api'
import events from '../../../../events'
import PageHeaderProduct from '../../../../components/page-headers/PageHeaderProduct'
import { useSession } from '../../../../api/auth'

import { Img } from './styles'
import EmailModal from './components/EmailModal'

const BACKGROUND_COLOR: Record<
  API.LibraryGuideColor,
  React.ComponentProps<typeof PageHeaderProduct>['backgroundColor']
> = {
  BLUE_01: 'PALE_BLUE_40',
  GREEN_01: 'GREEN_10',
  YELLOW_01: 'YELLOW_20',
  RED_01: 'RED_10',
  PINK_01: 'PINK_30',
}

type Props = {
  title: string
  description: string
  image: {
    url: string
    blurhash: string
  }
  slug: string
  onClickRead: () => void
  downloadUrl: string
  leadTag: string
  color: API.LibraryGuideColor
}

export const Header: React.FC<Props> = ({
  title,
  leadTag,
  description,
  onClickRead,
  image,
  slug,
  downloadUrl,
  color,
}) => {
  const [isLoggedIn] = useSession()
  const [modalIsOpen, setModalIsOpen] = React.useState(false)

  const imageProps = React.useMemo(
    () =>
      getImgProps(
        image.url,
        {
          small: 100,
          medium: 65,
          large: 40,
          xlarge: 40,
        },
        {
          fm: 'png8',
        },
      ),
    [image.url],
  )

  const modalImageProps = React.useMemo(
    () =>
      getImgProps(
        image.url,
        {
          small: 100,
          medium: 20,
          large: 20,
          xlarge: 20,
        },
        {
          fm: 'png8',
        },
      ),
    [image.url],
  )

  const handleEmailEntered = React.useCallback((email: string) => {
    events.emailSubmitted({ email })
  }, [])

  return (
    <>
      <PageHeaderProduct
        title={title}
        subtitleRichText={description}
        backgroundColor={BACKGROUND_COLOR[color]}
        button={{
          display: 'Download the guide',
          href: isLoggedIn ? downloadUrl : undefined,
          onClick: () => (!isLoggedIn ? setModalIsOpen(true) : undefined),
          isExternal: isLoggedIn,
        }}
        secondaryButtonText="Read it online"
        secondaryButtonOnClick={onClickRead}
      >
        <Img {...imageProps} blurHash={image.blurhash} alt="Library guide cover" />
      </PageHeaderProduct>

      {modalIsOpen && (
        <EmailModal
          title={title}
          onEmailEntered={handleEmailEntered}
          onClose={() => setModalIsOpen(false)}
          image={modalImageProps}
          downloadUrl={downloadUrl}
          leadTag={leadTag}
        />
      )}
    </>
  )
}

export default Header
